// Onboarding clarity / juice (SCORE-04) — word ON-LIFT verdict flash (§9.7 step 4).
//
// When a word trace is lifted the verdict was AUDIO-ONLY. This brief transient
// element flashes a confirmation: valid → Sun-Yellow→Plum confirm, invalid →
// crimson shake. ARCA-597 retired the floating ScorePill (it covered the grid),
// so the flash no longer mounts over the board — MatchScreen renders it centered
// over the staged-word chip area near Submit (a position:relative wrapper), and
// it fills that wrapper rather than carrying its own board-top offset.
//
// The optimistic on-lift outcome (valid iff trace ≥ config.minWordLength) is
// upgraded to 'invalid' if the server rejects the word (MatchScreen dispatches
// `runegambit:word-verdict` in the resp.phase_a_word_rejected branch, where the
// word-invalid SFX already fires).
//
// Reduced motion (UI-SPEC §5 row 'ScorePill valid state' + §9.7): NO shake, NO
// scale — a static colored outline holds for the lifetime, no animation. CSS
// keyframes live in pieces.css (scorePillConfirm / scorePillShake), forked by
// both @media prefers-reduced-motion and [data-motion=reduced].

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { $reducedMotion } from '../../stores/motion'
import { useAnnounce } from '../components/useAnnounce'

export type WordVerdict = 'valid' | 'invalid'

export interface ScorePillVerdictProps {
  readonly outcome: WordVerdict
  readonly lifetimeMs?: number
  readonly onDone: () => void
}

export function ScorePillVerdict({
  outcome,
  lifetimeMs = 600,
  onDone,
}: ScorePillVerdictProps): JSX.Element {
  const reducedMotion = useStore($reducedMotion)
  const intl = useIntl()
  const announce = useAnnounce()
  // The on-lift outcome is optimistic; a server rejection upgrades it to invalid.
  const [effective, setEffective] = useState<WordVerdict>(outcome)

  // SR: announce the verdict politely (the visible flash is decorative).
  useEffect(() => {
    announce(
      intl.formatMessage({
        id:
          effective === 'valid'
            ? 'a11y.announce.word_lift_valid'
            : 'a11y.announce.word_lift_invalid',
      }),
      'polite',
    )
  }, [effective, announce, intl])

  // Server rejection overrides the optimistic on-lift verdict while mounted.
  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<{ outcome?: WordVerdict }>).detail
      if (detail?.outcome === 'invalid') setEffective('invalid')
    }
    window.addEventListener('runegambit:word-verdict', handler)
    return () => {
      window.removeEventListener('runegambit:word-verdict', handler)
    }
  }, [])

  useEffect(() => {
    const id = window.setTimeout(onDone, lifetimeMs)
    return () => {
      window.clearTimeout(id)
    }
  }, [onDone, lifetimeMs])

  const isValid = effective === 'valid'

  return (
    <div
      data-screen-region="score-pill-verdict"
      data-verdict={effective}
      aria-hidden="true"
      style={{
        // ARCA-597 — centered over the chip wrapper (a position:relative
        // parent in MatchScreen) instead of the old board-top offset, so the
        // valid/invalid flash lands by the staged word near Submit, never over
        // the grid. pointer-events:none so it never swallows a Submit tap.
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        pointerEvents: 'none',
        minWidth: 'calc(var(--space-6) * 2)',
        height: 'calc(var(--space-5) + var(--space-2))',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      <div
        className="score-pill-verdict-flash"
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 'var(--radius-pill)',
          background: isValid
            ? 'linear-gradient(180deg, var(--color-cta-primary-top), var(--color-cta-primary), var(--color-cta-primary-deep))'
            : 'var(--color-cta-destructive)',
          // Reduced motion: static colored outline, no keyframe. Full motion:
          // the keyframe (pieces.css) runs the confirm flash / crimson shake.
          border: reducedMotion
            ? `3px solid ${isValid ? 'var(--color-magic)' : 'var(--color-cta-destructive)'}`
            : 'none',
          animation: reducedMotion
            ? 'none'
            : isValid
              ? 'scorePillConfirm 400ms ease-out forwards'
              : 'scorePillShake 300ms ease-out',
        }}
      />
    </div>
  )
}
